import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatPrice, type PurchaseSummary } from "@nh/shared";

/**
 * ═══════════════════════════════════════════════════════════════════
 * La card VIOLETA del dashboard (referencia "Overview" de Anahí,
 * 27-ago): la ganancia del sistema en el mes, dibujada día a día, con
 * los tres números del periodo abajo.
 *
 * La gráfica es SVG propio (sin librerías): UNA serie —los ingresos
 * MXN validados por día del resumen financiero, el mismo endpoint de
 * /admin/purchases— con línea clara sobre el violeta, área suave y el
 * punto más alto etiquetado con su chip, como en la referencia.
 * ═══════════════════════════════════════════════════════════════════
 */
export function RevenueOverviewCard({ summary }: { summary: PurchaseSummary | undefined }) {
  const { t } = useTranslation();

  const monthLabel = new Date().toLocaleDateString("es-MX", { month: "long", year: "numeric" });
  const revenueMXN = summary?.totals.revenue.find((row) => row.currency === "MXN");

  /*
   * ── Por qué se rellena el mes día a día ─────────────────────────────
   * `byDay` trae SOLO los días con compras. Con un día, la "línea" era un
   * punto —y un `path` de un punto no dibuja nada—, el punto quedaba en
   * el borde superior tapado por el chip del máximo, y no había fechas.
   * Se veía el chip flotando en una card vacía (Anahí, 28-ago).
   *
   * Ahora la serie va del día 1 del mes a hoy, con cero en los días sin
   * ventas: la línea siempre existe, el área también, y un día con
   * ventas se lee como un pico sobre el mes. Los puntos se pintan solo
   * en los días con ventas; el chip va ENCIMA del pico, no sobre él.
   */
  const chart = useMemo(() => {
    const days = summary?.byDay ?? [];
    if (days.length === 0) return null;
    const byDate = new Map(
      days.map((day) => [
        day.date,
        day.revenue.find((row) => row.currency === "MXN")?.amountCents ?? 0,
      ]),
    );
    const iso = (date: Date) => date.toISOString().slice(0, 10);
    const today = new Date();
    const minSeen = [...byDate.keys()].sort()[0]!;
    // Del 1 del mes (o del primer día con datos, si es anterior) a hoy.
    const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
    const first = minSeen < iso(monthStart) ? new Date(`${minSeen}T00:00:00.000Z`) : monthStart;
    const lastSeen = [...byDate.keys()].sort().at(-1)!;
    const last = lastSeen > iso(today) ? new Date(`${lastSeen}T00:00:00.000Z`) : today;
    const points: { date: string; cents: number }[] = [];
    for (let d = new Date(first); iso(d) <= iso(last); d.setUTCDate(d.getUTCDate() + 1)) {
      const date = iso(d);
      points.push({ date, cents: byDate.get(date) ?? 0 });
    }

    const W = 640;
    const H = 190;
    const TOP = 46; // sitio para el chip del máximo
    const BOTTOM = 26; // sitio para las fechas
    const SIDE = 16;
    const max = Math.max(...points.map((point) => point.cents), 1);
    const stepX = points.length > 1 ? (W - SIDE * 2) / (points.length - 1) : 0;
    const coords = points.map((point, index) => ({
      ...point,
      x: points.length > 1 ? SIDE + index * stepX : W / 2,
      y: H - BOTTOM - (point.cents / max) * (H - TOP - BOTTOM),
    }));
    const baseline = H - BOTTOM;
    let path = `M ${coords[0]!.x} ${coords[0]!.y}`;
    for (let index = 1; index < coords.length; index += 1) {
      const prev = coords[index - 1]!;
      const current = coords[index]!;
      const midX = (prev.x + current.x) / 2;
      path += ` C ${midX} ${prev.y}, ${midX} ${current.y}, ${current.x} ${current.y}`;
    }
    const area =
      coords.length > 1
        ? `${path} L ${coords[coords.length - 1]!.x} ${baseline} L ${coords[0]!.x} ${baseline} Z`
        : null;
    const peak = coords.reduce((best, point) => (point.cents > best.cents ? point : best));
    const sold = coords.filter((point) => point.cents > 0);
    // Marcas: primera, última y cada ~7 días entre medias.
    const tickEvery = Math.max(1, Math.round(coords.length / 5));
    const ticks = coords.filter(
      (_, index) => index === 0 || index === coords.length - 1 || index % tickEvery === 0,
    );
    return { W, H, path, area, coords, peak, sold, ticks, baseline };
  }, [summary]);

  const dayLabel = (date: string) => `${date.slice(8)}/${date.slice(5, 7)}`;

  return (
    <section
      className="relative overflow-hidden rounded-[28px] p-6 text-white shadow-ds-float"
      style={{
        background: "radial-gradient(130% 130% at 20% 0%, #6f5fe0 0%, #5C4FD6 45%, #43389f 100%)",
      }}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-[16px] font-semibold">{t("Ganancia del sistema")}</h2>
        <span className="rounded-ds-pill bg-white/15 px-3 py-1 text-[12px] font-medium capitalize">
          {monthLabel}
        </span>
      </div>

      {chart ? (
        <svg
          viewBox={`0 0 ${chart.W} ${chart.H}`}
          className="mt-3 w-full"
          role="img"
          aria-label={t("Ingresos validados por día del mes, en pesos")}
        >
          {/* Rejilla sutil entre el techo y la base. */}
          {[0.25, 0.5, 0.75].map((fraction) => (
            <line
              key={fraction}
              x1={12}
              x2={chart.W - 12}
              y1={chart.baseline - (chart.baseline - 46) * fraction}
              y2={chart.baseline - (chart.baseline - 46) * fraction}
              stroke="rgba(255,255,255,0.12)"
              strokeDasharray="3 6"
            />
          ))}
          <line
            x1={12}
            x2={chart.W - 12}
            y1={chart.baseline}
            y2={chart.baseline}
            stroke="rgba(255,255,255,0.25)"
          />
          {chart.area && <path d={chart.area} fill="rgba(255,255,255,0.14)" />}
          {chart.coords.length > 1 ? (
            <path
              d={chart.path}
              fill="none"
              stroke="rgba(255,255,255,0.92)"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : (
            /* Un solo día: una barra desde la base hasta el valor. */
            <line
              x1={chart.peak.x}
              x2={chart.peak.x}
              y1={chart.baseline}
              y2={chart.peak.y}
              stroke="rgba(255,255,255,0.92)"
              strokeWidth={6}
              strokeLinecap="round"
            />
          )}
          {/* Solo los días con ventas llevan punto: 30 puntos serían ruido. */}
          {chart.sold.map((point) => (
            <circle
              key={point.date}
              cx={point.x}
              cy={point.y}
              r={4.5}
              fill="#fff"
              stroke="#5C4FD6"
              strokeWidth={2}
            />
          ))}
          {/* Las fechas: primera, última y cada semana. */}
          {chart.ticks.map((point, index) => (
            <text
              key={point.date}
              x={point.x}
              y={chart.H - 6}
              fontSize={11}
              textAnchor={index === 0 ? "start" : index === chart.ticks.length - 1 ? "end" : "middle"}
              fill="rgba(255,255,255,0.6)"
            >
              {dayLabel(point.date)}
            </text>
          ))}
          {/* El chip del máximo, ENCIMA del pico (nunca tapándolo). */}
          {chart.peak.cents > 0 && (
            <g
              transform={`translate(${Math.min(Math.max(chart.peak.x - 59, 8), chart.W - 126)}, ${Math.max(chart.peak.y - 38, 4)})`}
            >
              <rect width={118} height={24} rx={12} fill="#191536" />
              <text x={59} y={16} textAnchor="middle" fontSize={12} fontWeight={600} fill="#fff">
                {formatPrice(chart.peak.cents, "MXN")}
              </text>
              <path
                d={`M ${59 - 5} 24 L 59 30 L ${59 + 5} 24 Z`}
                fill="#191536"
              />
            </g>
          )}
        </svg>
      ) : (
        <p className="mt-6 text-[13px] text-white/70">
          {t("Cuando el mes tenga compras validadas, aquí se dibujan día a día.")}
        </p>
      )}

      {/* ── Los tres números del periodo ──────────────────────────────── */}
      <div className="mt-4 grid grid-cols-3 gap-3 border-t border-white/15 pt-4 text-center">
        <div>
          <p className="text-[11px] uppercase tracking-ds-label text-white/60">{t("Compras")}</p>
          <p className="mt-0.5 text-[22px] font-semibold">{summary?.totals.purchases ?? "—"}</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-ds-label text-white/60">
            {t("Ingresos del mes")}
          </p>
          <p className="mt-0.5 text-[26px] font-semibold">
            {revenueMXN ? formatPrice(revenueMXN.amountCents, "MXN") : "—"}
          </p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-ds-label text-white/60">{t("Clientes")}</p>
          <p className="mt-0.5 text-[22px] font-semibold">{summary?.totals.clients ?? "—"}</p>
        </div>
      </div>
    </section>
  );
}
